import React from 'react';
import {getExecutorWebBaseUrl} from '@rocket-ui/lk-api';
import {readSessionCount} from 'features/auth/model/sessionCount';
import {Drawer} from 'shared/ui/Drawer';
import {fetchAuthorizedReceiptHtml, toAbsoluteExecutorUrl, toApiDocumentUrl, withApiDocumentBase} from './authorizedReceiptDocument';
import styles from './CreateReceiptDrawer.module.css';

export interface CreateReceiptDrawerProps {
	open: boolean;
	taskId: number | null;
	onClose: () => void;
}

function buildCreateReceiptUrl(taskId: number): string {
	const base = getExecutorWebBaseUrl().replace(/\/$/, '');
	const query = new URLSearchParams({tid: String(taskId)});

	return toAbsoluteExecutorUrl(`${base}/fns_receipts/new?${query.toString()}`);
}

function isClosedReceiptMessage(data: unknown, taskId: number): boolean {
	if (typeof data !== 'object' || data == null) return false;

	const message = data as {type?: unknown; status?: unknown; task_id?: unknown};

	return message.type === 'fns_receipt_page' && message.status === 'closed' && Number(message.task_id) === taskId;
}

export function CreateReceiptDrawer({open, taskId, onClose}: CreateReceiptDrawerProps): React.ReactElement {
	const isOpen = open && taskId != null;
	const [srcDoc, setSrcDoc] = React.useState<string>();
	const [error, setError] = React.useState<string | null>(null);
	const [isLoading, setIsLoading] = React.useState(false);
	const requestSeq = React.useRef(0);
	const frameRef = React.useRef<HTMLIFrameElement>(null);

	React.useEffect(() => {
		if (!isOpen || taskId == null) return;

		const onMessage = (event: MessageEvent) => {
			if (event.source !== frameRef.current?.contentWindow) return;
			if (!isClosedReceiptMessage(event.data, taskId)) return;

			onClose();
		};

		window.addEventListener('message', onMessage);
		return () => window.removeEventListener('message', onMessage);
	}, [isOpen, taskId, onClose]);

	React.useEffect(() => {
		if (!isOpen || taskId == null) {
			requestSeq.current += 1;
			setSrcDoc(undefined);
			setError(null);
			setIsLoading(false);
			return;
		}

		const seq = ++requestSeq.current;
		const pageUrl = buildCreateReceiptUrl(taskId);
		setIsLoading(true);
		setError(null);

		void fetchAuthorizedReceiptHtml({url: pageUrl})
			.then((html) => {
				if (seq !== requestSeq.current) return;
				setSrcDoc(withApiDocumentBase(html, toApiDocumentUrl(pageUrl)));
			})
			.catch(() => {
				if (seq !== requestSeq.current) return;
				setError('Не получилось открыть форму чека. Попробуйте ещё раз');
			})
			.finally(() => {
				if (seq === requestSeq.current) setIsLoading(false);
			});
	}, [isOpen, taskId]);

	return (
		<Drawer open={isOpen} onClose={onClose} bodyClassName={styles.body} panelClassName={styles.panel}>
			{error ? <p className={styles.status}>{error}</p> : null}
			{!error && isLoading && !srcDoc ? <p className={styles.status}>Загрузка...</p> : null}
			{srcDoc ? (
				<iframe
					ref={frameRef}
					className={styles.frame}
					srcDoc={srcDoc}
					data-session-count={readSessionCount()}
					title="Создать чек"
				/>
			) : null}
		</Drawer>
	);
}
